import { type FC } from 'react';
import { Grid } from '@mui/material';
import { TFunction } from 'i18next';
import LongCard from 'page-sections/dashboards/CRM/LongCard';
import { useSeason } from 'contexts/SeasonContext';
import { calendarEntriesApi } from 'api';
import { CalendarEntryEntityTypeEnum } from 'api/generated';
import { useEffect, useState } from 'react';

interface DataProps {
    contract: {
        startDate: string,
        endDate: string,
        days: number,
        totalDaysOff: number,
        todayDaysOff: number,
        daysOff: number,
    },
    agreements?: any[],
    employeeId?: number,
    selectedDate?: Date,
    t: TFunction;
}

const EmployeeSeasonDaysOff: FC<DataProps> = (props) => {
    const { t } = props;
    const season = useSeason();
    const [seasonDaysOff, setSeasonDaysOff] = useState({
        used: 0,
        earned: 0,
        willEarn: 0,
    });

    useEffect(() => {
        const calculateSeasonDaysOff = async () => {
            if (!season.seasonId || !props.employeeId) return;

            const currentSeason = season.getSeason();
            if (!currentSeason) return;



            const seasonStartDate = new Date(currentSeason.startDate);
            const seasonEndDate = new Date(currentSeason.endDate);
            const selectedDateForCalculation = props.selectedDate || new Date();

            const seasonEvents = (await calendarEntriesApi.findEventsPerPeriodOfTime(
                seasonStartDate.toDateString(),
                selectedDateForCalculation.toDateString(),
                props.employeeId,
                season.seasonId.toString()
            )).data as Record<string, number>;

            const usedDaysOff = seasonEvents[CalendarEntryEntityTypeEnum.DayOff] + (seasonEvents[CalendarEntryEntityTypeEnum.HalfDay] || 0) * 0.5;

            let totalSeasonDaysOff = 0;
            let earnedSeasonDaysOff = 0;

            if (props.agreements && Array.isArray(props.agreements)) {
                for (const agreement of props.agreements) {
                    const agreementStartDate = new Date(agreement.startedOn);
                    const agreementEndDate = agreement.endedOn ? new Date(agreement.endedOn) : seasonEndDate;

                    const seasonAgreementStartDate = Math.max(seasonStartDate.getTime(), agreementStartDate.getTime());
                    const seasonAgreementEndDate = Math.min(seasonEndDate.getTime(), agreementEndDate.getTime());

                    if (seasonAgreementStartDate <= seasonAgreementEndDate) {
                        const duration = Math.floor((seasonAgreementEndDate - seasonAgreementStartDate) / (1000 * 60 * 60 * 24)) + 1;
                        const fromStart = Math.floor((selectedDateForCalculation.getTime() - seasonAgreementStartDate) / (1000 * 60 * 60 * 24)) + 1;

                        const daysOffPerWeek = agreement.createDaysAutomatically ?
                            agreement.freeDays.filter((elem: any, index: number, self: any[]) => {
                                return index === self.indexOf(elem);
                            }).length : agreement.daysOff;

                        totalSeasonDaysOff += daysOffPerWeek * duration / 7;
                        earnedSeasonDaysOff += daysOffPerWeek * (fromStart > duration ? duration : fromStart) / 7;
                    }
                }
            }

            const earnedDaysOff = Math.max(0, earnedSeasonDaysOff);

            setSeasonDaysOff({
                used: usedDaysOff,
                earned: earnedDaysOff,
                willEarn: totalSeasonDaysOff
            });
        };

        calculateSeasonDaysOff();
    }, [season.seasonId, props.contract]);

    const convertToFraction = (value: number) => {
        const days = Math.floor(value);

        let workDaysFraction = "";

        if (value >= days + 0.25)
            workDaysFraction = "¼";
        if (value >= days + 0.5)
            workDaysFraction = "½";

        return String(days) + workDaysFraction;
    }

    const statsList = [
        {
            id: 1,
            amount: !isNaN(seasonDaysOff.used) ? convertToFraction(seasonDaysOff.used) : "-",
            title: t('employees.report.contract.used'),
            color: '#5479F7',
        },
        {
            id: 2,
            amount: !isNaN(seasonDaysOff.earned) ? convertToFraction(seasonDaysOff.earned) : "-",
            title: t('employees.report.contract.due'),
            color: '#E5402A',
        },
        {
            id: 3,
            amount: !isNaN(seasonDaysOff.willEarn) ? convertToFraction(seasonDaysOff.willEarn) : "-",
            title: t('employees.report.contract.total'),
            color: '#FF9C12',
        },
    ];

    return (
        <Grid item md={12} xs={12}>
            <LongCard list={statsList} longCardTitle={t('employees.report.contract.seasonDaysOffTitle')} />
        </Grid>
    );
};

export default EmployeeSeasonDaysOff;
